import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View, useWindowDimensions } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { ArrowIcon, Button as Button95, Hourglass, NumberInput, Panel, Text, TextInput } from 'react95-native-rabbl';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import { GooglePlacesAutocomplete, Place } from '@/components/react-native-google-places-autocomplete/GooglePlacesAutocomplete';
//import { GooglePlacesAutocomplete, Place } from 'react-native-google-places-autocomplete';
import Constants from 'expo-constants';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { useHeaderHeight } from "expo-router/react-navigation";
import { Meta } from '@/components/Meta';
import { AppText } from '@/components/AppText';

const MIN_LIST_HEIGHT = 250;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
    minWidth: AppConsts.BASIC_MINIMUM_WIDTH,
    maxWidth: AppConsts.MAX_WIDTH_COMFORTABLE_READING,
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
  inputWrapper: {
    gap: 10,
    alignItems: 'center',
    minHeight: MIN_LIST_HEIGHT,
  },
});

export default function Location() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const router = useRouter();
  //const locationInput = useRef<RNTextInput>(null);
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const dispatch = useDispatch();
  const {height, width} = useWindowDimensions();
  const headerHeight = useHeaderHeight();
  const { edit } = useLocalSearchParams<{ edit?: string }>();

  const listHeight = MIN_LIST_HEIGHT;

  const onSubmit = useCallback(async () => {
    try {
      showSpinner();

      const response = await AppUtilities.fetchAuthorized(
        "Location.onSubmit",
        "/rabbl/api/set_location",
        "POST",
        {
          domain: domain,
          name: address,
          latitude: latitude,
          longitude: longitude,
        }
      );
      const membership: CommunityMembership = ((response as any).body as any).community_membership;
      dispatch(setData(response));
      if (edit && edit.length > 0 && router.canGoBack()) {
        router.back();
      } else {
        router.navigate(AppUtils.getProfileCreateNextPageLink(membership) as RelativePathString);
      }

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(), true);
      AppUtilities.handleException(e, "Location.onSubmit");
    } finally {
      hideSpinner();
    }
  }, [domain, address, latitude, longitude]);

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  return (
    <BasicScrollView center={true}>
      <Meta title={"Your city"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>

          <AppText>What's your city?</AppText>

          <View style={[styles.inputWrapper, { height: listHeight }]}>
            <GooglePlacesAutocomplete
              placeholder="City"
              minLength={2}
              debounce={200}
              //autoFocus={false}
              //ref={locationInput}
              predefinedPlaces={
                !AppConstants.IS_DEVELOPMENT ?
                    []
                  :
                    [
                      {
                        description: "New Braunfels, TX",
                        geometry: {
                          location: {
                            lat: 29.702566,
                            lng: -98.124063,
                          }
                        }
                      }
                    ] as Place[]
              }
              //keepResultsAfterBlur={true}
              //listViewDisplayed={true}
              fetchDetails={true}
              styles={{
                container: {
                },
                description: {
                  fontWeight: "bold",
                  fontFamily: 'sans-serif',
                },
                listView: {
                  backgroundColor: 'transparent',
                },
                textInput: {
                  backgroundColor: "transparent",
                  fontFamily: 'sans-serif',
                },
                textInputContainer: {
                  width: "100%",
                  backgroundColor: "transparent",
                  borderWidth: 1,
                  //borderColor: "#717171",
                  borderRadius: 5,
                },
                row: {
                  backgroundColor: "transparent",
                }
              }}
              textInputProps={{ placeholderTextColor: "#717171" }}
              query={{
                key: Constants.expoConfig?.extra?.googleNoRestrictionsKey,
                language: "en",
                types: "(regions)", // https://developers.google.com/maps/documentation/places/web-service/legacy/supported_types
              }}
              //currentLocation={true}
              //currentLocationLabel='Current location'
              onPress={(data, details) => {
                let addr = null;
                if (details) {
                  addr = details.formatted_address;
                  if (!addr || addr.length == 0) {
                    addr = (details as any).description;
                  }
                }

                if (details && addr && details.geometry && details.geometry.location) {
                  setAddress(addr);
                  setLatitude(details.geometry.location.lat);
                  setLongitude(details.geometry.location.lng);
                } else {
                  AppUtilities.info('Geolocation results:');
                  AppUtilities.dir(data);
                  AppUtilities.dir(details);
                  AppUtilities.handleError('Could not get location details', 'location.onPress');
                  AppUtils.showErrorToast('Error getting location deatils', null, true);
                }
              }}
              GooglePlacesDetailsQuery={{
                fields: "formatted_address,geometry",
              }}
              enablePoweredByContainer={false}
              timeout={10000} //ms
              onFail={(error) => {
                AppUtils.showErrorToast("Failed lookup: " + error, null, true);
              }}
              onTimeout={() => {
                AppUtils.showErrorToast("Timed out. Check your internet connection.", null, true);
              }}
              requestUrl={{
                useOnPlatform: 'web',
                url: AppUtilities.getActualHostLink(false, false) + '/proxy/https://maps.googleapis.com/maps/api',
              }}
            />
          </View>

          <View style={styles.buttonWrapper}>
            <Button95 onPress={onSubmit} disabled={address.length == 0 || latitude == 0 || longitude == 0}>{ edit ? "Save" : "Next" }</Button95>
          </View>
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
